import React from 'react'

import { Field } from '../../components/Field'
import { Input } from '../../components/Input'
import { ModalProvider, useModal } from '../../components/Modal'
import { Selections } from '../../components/Selections'
import { useExtractSpaceProps } from '../../hooks'
import { Calendar } from '../../icons'
import { SpaceProps } from '../../themes'
import {
  CalendarInputModal,
  MultipleCalendarInputModalProps,
} from '../CalendarInputModal'
import {
  filterDates,
  getSelectionLabel,
  groupConsecutiveDates,
  parseDateSelection,
} from './utils'

export interface CalendarInputModalFieldProps
  extends Omit<
      MultipleCalendarInputModalProps,
      'title' | 'initialDate' | 'onSubmit'
    >,
    SpaceProps {
  /**
   * Input ID
   */
  id?: string

  /**
   * Label and modal title
   */
  label: string

  /**
   * From date
   */
  fromDate?: MultipleCalendarInputModalProps['fromDate']

  /**
   * To date
   */
  toDate?: MultipleCalendarInputModalProps['toDate']

  /**
   * Selected date values
   */
  value?: MultipleCalendarInputModalProps['initialDate']

  /**
   * Icon to display before the error message
   */
  errorIcon?: React.ReactNode

  /**
   * Error messages
   */
  errorMessages?: string | string[]

  /**
   * Icon to display before the caption message
   */
  captionIcon?: React.ReactNode

  /**
   * Caption
   */
  captionMessages?: string | string[]

  /**
   * onChange handler
   */
  onChange?: MultipleCalendarInputModalProps['onSubmit']
}

const InnerCalendarInputModalField = ({
  id,
  value,
  label,
  // options,
  fromDate,
  toDate,
  errorIcon,
  errorMessages,
  captionIcon,
  captionMessages,
  onChange,
  expandOnSmallDevices,
  ...rest
}: CalendarInputModalFieldProps) => {
  const autoId = React.useId()
  const inputId = id ?? autoId
  const { openModal } = useModal()
  const [spaceProps, modalProps] = useExtractSpaceProps(rest)

  const openCalendarInputModal = () => {
    openModal(CalendarInputModal, {
      ...modalProps,
      mode: 'multiple',
      title: label,
      expandOnSmallDevices: expandOnSmallDevices ?? true,
      initialDate: value,
      fromDate,
      toDate,
      onSubmit: (dates: Date[]) => {
        onChange?.(dates)
      },
    })
  }

  const selected = value && value.length > 0

  const consecutiveDates = groupConsecutiveDates(value ?? [])

  return (
    <Field {...spaceProps}>
      <Field.Label id={inputId}>{label}</Field.Label>
      <Field.Content mt={selected ? 3 : 0}>
        {!selected ? (
          <Input
            variant="button"
            placeholder="選択する"
            onClick={openCalendarInputModal}
            iconAfter={<Calendar />}
          />
        ) : (
          <Selections
            value={consecutiveDates.map(getSelectionLabel)}
            onClick={openCalendarInputModal}
            onClear={(targetDateStr) => {
              const dates = parseDateSelection(targetDateStr)
              onChange?.(filterDates(value ?? [], dates))
            }}
          />
        )}
      </Field.Content>

      <Field.Messages
        mt={2}
        type="error"
        messages={errorMessages}
        icon={errorIcon}
      />

      <Field.Messages
        mt={errorMessages || (errorMessages && errorMessages.length) ? 1 : 2}
        type="caption"
        messages={captionMessages}
        icon={captionIcon}
      />
    </Field>
  )
}

export const CalendarInputModalField = (
  props: CalendarInputModalFieldProps,
) => {
  return (
    <ModalProvider>
      <InnerCalendarInputModalField {...props} />
    </ModalProvider>
  )
}
